// src/pages/enterprise-ai/agentBuilderData.js
//
// Dummy data for the Agent Builder page. Shaped field for field like a real
// unitedDomainAgents document: every state carries name, artifact,
// narrationTone, pillStrategy, haltTurn, allowedExits, requiredEnteringMemory,
// requiredExitingMemory, tools and prompt.
//
// `label` is the one addition that is not part of the document today. It is the
// plain-English name a human reads on the chip; `name` stays the identifier the
// runtime switches on. It is optional, and a state without one falls back to
// showing its identifier.
//
// State-level `tools` are plain STRING names. That matches the runtime
// document (backend/services/genesisDeployHelpers.js flattenSpecForRuntime
// collapses tools to names on deploy), not the review-time
// { name, status } objects Genesis extraction emits.
//
// The data is seeded so issues are visible on first render:
//   travel_booking_agent   dead exit, unreachable state, ghost artifact,
//                          untiered money tool
//   marketing_agent        undispatchable tools, a halt state that cannot ask
//   healthcare_agent       empty prompts
//
// Nothing here is fetched and nothing here is written.
//
// WIRE: GET /api/internal/agents (exists, lean only)
//   Replaces AGENTS as the left rail source. The lean payload supplies
//   agentId, displayName, iconHint, version and counts only. It does NOT
//   carry states, so it can fill the rail but not the center or right column.
//
// WIRE: GET /api/internal/agents/:id/full (must be built, T1)
//   Replaces the `states` array on the selected agent. Must return the whole
//   unitedDomainAgents document including per-state prompt and the memory
//   contract, or the inspector has nothing to show.
//
//   It must also serve `label` per state, defaulting to the state identifier
//   when the agent doc has no label for it. The chip reads label first and
//   falls back to name, so an endpoint that omits the field degrades to
//   today's identifier-only rendering rather than showing blank chips. That
//   means `label` needs a home on the agent document, not just in this fixture.
//
// WIRE: GET /api/internal/agents/tools (must be built, T1)
//   Replaces TOOL_CATALOG. Must return dispatchable, money and tier for every
//   tool. Without those three fields the UNDISPATCHABLE and UNTIERED_MONEY
//   issue rules cannot fire at all.

// ---------------------------------------------------------------------------
// Tool catalog
// ---------------------------------------------------------------------------
// dispatchable: the dispatcher can actually invoke it today.
// money:        invoking it moves real money.
// tier:         spend tier gate. A money tool with a null tier is ungated.

export const TOOL_CATALOG = [
  { name: 'lookupTravelerProfile', description: 'Read the traveler profile, home airport and seat preferences.', dispatchable: true, money: false, tier: null },
  { name: 'captureTripRequest', description: 'Record origin, destination, dates and cabin from the conversation.', dispatchable: true, money: false, tier: null },
  { name: 'evaluateTravelPolicy', description: 'Score a trip request against the org travel policy.', dispatchable: true, money: false, tier: null },
  { name: 'requestManagerApproval', description: 'Open an approval request against the traveler cost centre owner.', dispatchable: true, money: false, tier: null },
  { name: 'searchFlightInventory', description: 'Query the GDS for bookable fares on the requested route.', dispatchable: true, money: false, tier: null },
  { name: 'holdSeatAssignment', description: 'Place a courtesy hold on a seat for up to twenty minutes.', dispatchable: true, money: false, tier: null },
  { name: 'authorizeCorporateCard', description: 'Authorize a charge against the org virtual card.', dispatchable: true, money: true, tier: 'tier_2' },
  { name: 'increase_manual_purchase_amount', description: 'Raise the manual purchase ceiling for the current booking.', dispatchable: true, money: true, tier: null },
  { name: 'issueTicket', description: 'Ticket the held itinerary and capture the authorized funds.', dispatchable: true, money: true, tier: 'tier_3' },
  { name: 'emailItinerary', description: 'Send the confirmed itinerary to the traveler and the approver.', dispatchable: true, money: false, tier: null },
  { name: 'enrollLoyaltyProgram', description: 'Attach or create a loyalty number on the carrier record.', dispatchable: true, money: false, tier: null },

  { name: 'captureCampaignBrief', description: 'Record objective, market, timing and success metric for a campaign.', dispatchable: true, money: false, tier: null },
  { name: 'querySegmentIndex', description: 'Look up an audience segment by trait and size.', dispatchable: false, money: false, tier: null },
  { name: 'setCampaignBudget', description: 'Commit a campaign budget against the marketing cost centre.', dispatchable: true, money: true, tier: 'tier_1' },
  { name: 'pushCreativeToAdPlatform', description: 'Upload approved creative to the downstream ad platform.', dispatchable: false, money: false, tier: null },

  { name: 'triagePatientIntake', description: 'Classify an inbound patient message by urgency and care type.', dispatchable: true, money: false, tier: null },
  { name: 'verifyInsuranceEligibility', description: 'Check plan eligibility and remaining benefit for a member.', dispatchable: true, money: false, tier: null },
  { name: 'bookClinicVisit', description: 'Reserve a clinic slot against the provider calendar.', dispatchable: true, money: false, tier: null },
  { name: 'escalateToCareTeam', description: 'Page the on-call care team with the triage summary.', dispatchable: true, money: false, tier: null },
];

// ---------------------------------------------------------------------------
// Artifact catalog
// ---------------------------------------------------------------------------
// registered: the artifact has a card renderer bound to it. An unregistered
// artifact renders as nothing in the client, so binding one is an issue.

export const ARTIFACT_CATALOG = [
  { name: 'traveler_profile', registered: true, description: 'Traveler identity, home airport and seat preferences.' },
  { name: 'trip_request', registered: true, description: 'Origin, destination, dates and cabin.' },
  { name: 'policy_verdict', registered: true, description: 'In policy, out of policy, or needs approval.' },
  { name: 'approval_decision', registered: true, description: 'Approver identity, verdict and timestamp.' },
  { name: 'flight_options', registered: true, description: 'Ranked bookable fares with price and duration.' },
  { name: 'seat_hold', registered: true, description: 'Held seat, cabin and hold expiry.' },
  { name: 'payment_authorization', registered: true, description: 'Authorized amount, card token and tier used.' },
  { name: 'flight_booking', registered: true, description: 'Carrier record locator and ticketed segments.' },
  { name: 'itinerary', registered: true, description: 'Final itinerary as sent to the traveler.' },
  { name: 'loyalty_enrollment', registered: false, description: 'Loyalty number attached to the carrier record.' },

  { name: 'campaign_brief', registered: true, description: 'Objective, market, timing and success metric.' },
  { name: 'audience_segment', registered: true, description: 'Selected segment, traits and reachable size.' },
  { name: 'campaign_budget', registered: true, description: 'Committed budget and cost centre.' },
  { name: 'creative_set', registered: true, description: 'Approved creative variants for the campaign.' },

  { name: 'patient_intake', registered: true, description: 'Urgency, symptoms and care type.' },
  { name: 'eligibility_result', registered: true, description: 'Plan eligibility and remaining benefit.' },
  { name: 'scheduled_visit', registered: true, description: 'Provider, location and appointment time.' },
  { name: 'care_escalation', registered: true, description: 'Escalation summary handed to the on-call team.' },
];

// ---------------------------------------------------------------------------
// travel_booking_agent, 11 states
// ---------------------------------------------------------------------------

const TRAVEL_BOOKING_AGENT = {
  agentId: 'travel_booking_agent',
  displayName: 'Travel Booking Agent',
  iconHint: '✈️',
  version: 7,
  activeVersion: 6,
  tagline: 'Books policy-compliant business travel end to end.',
  description:
    'Handles corporate air travel from the first request to the issued ticket. Reads the traveler profile, scores the trip against policy, routes anything out of policy to the cost centre owner, then searches, holds, authorizes, books and sends the itinerary.',
  gatheringType: 'corporate',
  capabilityName: 'travel.booking',
  router: {
    intentDescription:
      'Routes here when the caller wants to book, change or price business air travel, asks about travel policy, or needs manager approval for a trip.',
  },
  voicePreamble: {
    tone: 'efficient, precise, never pushy',
    bannedPhrases: ['that is not allowed', 'you cannot do that'],
    preferredPhrases: ['let me check policy on that', 'I can hold that seat while we confirm'],
  },
  initialState: 'PROFILE_GATE',
  completedStates: ['trip_complete'],
  escalatedStates: ['trip_declined'],
  receivesHandoffs: ['relay_agent'],
  initiatesHandoffs: ['expense_report_agent'],
  ownedArtifacts: [
    'traveler_profile', 'trip_request', 'policy_verdict', 'approval_decision',
    'flight_options', 'seat_hold', 'payment_authorization', 'flight_booking', 'itinerary',
  ],
  states: [
    {
      name: 'PROFILE_GATE',
      label: 'Profile check',
      artifact: 'traveler_profile',
      narrationTone: 'warm',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: ['TRIP_INTAKE'],
      requiredEnteringMemory: [],
      requiredExitingMemory: ['travelerId', 'homeAirport'],
      tools: ['lookupTravelerProfile'],
      prompt:
        'Confirm who is travelling before anything else. Read the profile back in one line, home airport and seat preference included, and ask the traveler to confirm it is still correct. Do not proceed on a profile the traveler has not confirmed.',
    },
    {
      name: 'TRIP_INTAKE',
      label: 'Gather trip details',
      artifact: 'trip_request',
      narrationTone: 'efficient',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['POLICY_CHECK'],
      requiredEnteringMemory: ['travelerId', 'homeAirport'],
      requiredExitingMemory: ['origin', 'destination', 'departDate', 'returnDate', 'cabin'],
      tools: ['captureTripRequest'],
      prompt:
        'Collect the trip in as few turns as possible. Offer the home airport as the default origin. If the traveler gives a vague date, ask for the exact one rather than guessing. Never invent a return date for a one way request.',
    },
    {
      name: 'POLICY_CHECK',
      label: 'Policy check',
      artifact: 'policy_verdict',
      narrationTone: 'neutral',
      pillStrategy: 'single',
      haltTurn: false,
      allowedExits: ['SEARCH_FLIGHTS', 'MANAGER_APPROVAL_PENDING'],
      requiredEnteringMemory: ['origin', 'destination', 'departDate', 'cabin'],
      requiredExitingMemory: ['policyVerdict'],
      tools: ['evaluateTravelPolicy'],
      prompt:
        'Score the trip against the org travel policy and state the verdict plainly. In policy goes straight to search. Out of policy goes to approval, and you say which rule it breaks and by how much. Never describe a policy breach as a rejection.',
    },
    {
      name: 'MANAGER_APPROVAL_PENDING',
      label: 'Waiting on manager',
      artifact: 'approval_decision',
      narrationTone: 'reassuring',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: ['SEARCH_FLIGHTS', 'trip_declined'],
      requiredEnteringMemory: ['policyVerdict', 'travelerId'],
      requiredExitingMemory: ['approvalDecision', 'approverId'],
      tools: ['requestManagerApproval'],
      prompt:
        'Open the approval and tell the traveler exactly who it went to and what happens next. While it is open, do not hold seats and do not quote a fare as if it were confirmed. If the approver declines, say so once, without editorial.',
    },
    {
      name: 'SEARCH_FLIGHTS',
      label: 'Search flights',
      artifact: 'flight_options',
      narrationTone: 'efficient',
      pillStrategy: 'multi',
      haltTurn: false,
      allowedExits: ['PRESENT_OPTIONS'],
      requiredEnteringMemory: ['origin', 'destination', 'departDate', 'cabin'],
      requiredExitingMemory: ['searchResultId'],
      tools: ['searchFlightInventory'],
      prompt:
        'Search bookable inventory only. Rank on total travel time first and price second, unless the traveler asked for the cheapest fare outright. Drop anything the policy verdict already ruled out.',
    },
    {
      name: 'PRESENT_OPTIONS',
      label: 'Show flight options',
      artifact: 'flight_options',
      narrationTone: 'upbeat',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['SEAT_SELECTION', 'SEARCH_FLIGHTS'],
      requiredEnteringMemory: ['searchResultId'],
      requiredExitingMemory: ['selectedFareId'],
      tools: [],
      prompt:
        'Show at most three options. For each one give carrier, depart and arrive local times, stops and total price. Say which one you would take and why in a single sentence. If none fit, offer to search again rather than pushing the closest miss.',
    },
    {
      name: 'SEAT_SELECTION',
      label: 'Pick seats',
      artifact: 'seat_hold',
      narrationTone: 'friendly',
      pillStrategy: 'single',
      haltTurn: true,
      // SEAT_MAP_RETRY is not a state on this agent and is not a terminal
      // label. Seeded so the DEAD_EXIT rule fires on first render.
      allowedExits: ['PAYMENT_CONFIRM', 'SEAT_MAP_RETRY'],
      requiredEnteringMemory: ['selectedFareId', 'travelerId'],
      requiredExitingMemory: ['seatHoldId'],
      tools: ['holdSeatAssignment'],
      prompt:
        'Offer seats that match the stored preference first. Say the hold expiry out loud so the traveler knows the clock is running. Never hold more than one seat per segment.',
    },
    {
      name: 'PAYMENT_CONFIRM',
      label: 'Confirm payment',
      artifact: 'payment_authorization',
      narrationTone: 'calm',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: ['BOOK_FLIGHT'],
      requiredEnteringMemory: ['seatHoldId', 'selectedFareId'],
      requiredExitingMemory: ['authorizationId', 'authorizedAmount'],
      // increase_manual_purchase_amount is money true with a null tier in the
      // catalog. Seeded so the UNTIERED_MONEY rule fires on first render.
      tools: ['authorizeCorporateCard', 'increase_manual_purchase_amount'],
      prompt:
        'State the exact amount you are about to authorize and wait for a yes. Authorize once. If the authorization fails, say what failed and stop. Do not retry a declined card without being asked.',
    },
    {
      name: 'BOOK_FLIGHT',
      label: 'Book the flight',
      artifact: 'flight_booking',
      narrationTone: 'confident',
      pillStrategy: 'single',
      haltTurn: false,
      allowedExits: ['ISSUE_ITINERARY'],
      requiredEnteringMemory: ['authorizationId', 'seatHoldId'],
      requiredExitingMemory: ['recordLocator'],
      tools: ['issueTicket'],
      prompt:
        'Ticket the held itinerary against the authorization already captured. Read the record locator back once, slowly. If ticketing fails after the card was authorized, escalate immediately rather than reauthorizing.',
    },
    {
      name: 'ISSUE_ITINERARY',
      label: 'Send the itinerary',
      artifact: 'itinerary',
      narrationTone: 'warm',
      pillStrategy: 'single',
      haltTurn: false,
      allowedExits: ['trip_complete'],
      requiredEnteringMemory: ['recordLocator'],
      requiredExitingMemory: ['itinerarySentAt'],
      tools: ['emailItinerary'],
      prompt:
        'Send the itinerary to the traveler and to the approver if one was involved. Confirm delivery in one line and close out. Do not upsell anything at this point.',
    },
    {
      name: 'LOYALTY_CAPTURE',
      label: 'Save loyalty number',
      // loyalty_enrollment is registered false in the artifact catalog, and
      // no state exits to LOYALTY_CAPTURE. Seeded so GHOST_ARTIFACT and
      // UNREACHABLE both fire on first render.
      artifact: 'loyalty_enrollment',
      narrationTone: 'friendly',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: ['ISSUE_ITINERARY'],
      requiredEnteringMemory: ['travelerId'],
      requiredExitingMemory: ['loyaltyNumber'],
      tools: ['enrollLoyaltyProgram'],
      prompt:
        'Ask once whether the traveler wants their loyalty number on this booking. One ask. If they decline or do not answer, move on and never raise it again in this conversation.',
    },
  ],
};

// ---------------------------------------------------------------------------
// marketing_agent, 5 states
// ---------------------------------------------------------------------------

const MARKETING_AGENT = {
  agentId: 'marketing_agent',
  displayName: 'Marketing Agent',
  iconHint: '📣',
  version: 3,
  activeVersion: 3,
  tagline: 'Turns a campaign brief into a launched, budgeted campaign.',
  description:
    'Takes a marketing objective in plain language, picks the audience segment, commits a budget against the marketing cost centre, walks creative through review, and launches to the downstream ad platform.',
  gatheringType: 'corporate',
  capabilityName: 'marketing.campaign',
  router: {
    intentDescription:
      'Routes here when the caller wants to launch, budget or adjust a marketing campaign, asks about audience segments, or needs creative pushed to an ad platform.',
  },
  voicePreamble: {
    tone: 'direct, commercially literate',
    bannedPhrases: ['this will definitely convert', 'guaranteed reach'],
    preferredPhrases: ['based on the last comparable campaign', 'here is what that budget buys'],
  },
  initialState: 'BRIEF_INTAKE',
  completedStates: ['campaign_live'],
  escalatedStates: ['campaign_blocked'],
  receivesHandoffs: [],
  initiatesHandoffs: [],
  ownedArtifacts: ['campaign_brief', 'audience_segment', 'campaign_budget', 'creative_set'],
  states: [
    {
      name: 'BRIEF_INTAKE',
      label: 'Take the brief',
      artifact: 'campaign_brief',
      narrationTone: 'direct',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['AUDIENCE_SELECT'],
      requiredEnteringMemory: [],
      requiredExitingMemory: ['objective', 'market', 'successMetric'],
      tools: ['captureCampaignBrief'],
      prompt:
        'Get the objective, the market and the one metric that decides whether this worked. Push back on a brief with no metric rather than accepting it and inventing one later.',
    },
    {
      name: 'AUDIENCE_SELECT',
      label: 'Pick the audience',
      artifact: 'audience_segment',
      narrationTone: 'analytical',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['BUDGET_SET'],
      requiredEnteringMemory: ['objective', 'market'],
      requiredExitingMemory: ['segmentId', 'reachableSize'],
      // querySegmentIndex is dispatchable false in the catalog. Seeded so the
      // UNDISPATCHABLE rule fires on first render.
      tools: ['querySegmentIndex'],
      prompt:
        'Offer two or three segments with their reachable size, never a single take-it-or-leave-it option. If reachable size is under the objective, say so before the budget conversation, not after.',
    },
    {
      name: 'BUDGET_SET',
      label: 'Set the budget',
      artifact: 'campaign_budget',
      narrationTone: 'neutral',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: ['CREATIVE_REVIEW'],
      requiredEnteringMemory: ['segmentId', 'reachableSize'],
      requiredExitingMemory: ['budgetAmount', 'costCentre'],
      tools: ['setCampaignBudget'],
      prompt:
        'State what the proposed budget buys against this segment in concrete terms before committing it. Commit once, on an explicit yes, and name the cost centre it lands on.',
    },
    {
      name: 'CREATIVE_REVIEW',
      label: 'Review the creative',
      artifact: 'creative_set',
      narrationTone: 'collaborative',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['LAUNCH_CONFIRM'],
      requiredEnteringMemory: ['budgetAmount', 'segmentId'],
      requiredExitingMemory: ['approvedCreativeIds'],
      // pushCreativeToAdPlatform is dispatchable false in the catalog.
      tools: ['pushCreativeToAdPlatform'],
      prompt:
        'Walk each creative variant past the reviewer one at a time. Record which variants are approved by id. Do not push anything that has not been approved by name.',
    },
    {
      name: 'LAUNCH_CONFIRM',
      label: 'Confirm the launch',
      // haltTurn true with no exits and no artifact. Seeded so the
      // HALT_NO_ASK rule fires on first render.
      artifact: null,
      narrationTone: 'direct',
      pillStrategy: 'single',
      haltTurn: true,
      allowedExits: [],
      requiredEnteringMemory: ['approvedCreativeIds', 'budgetAmount'],
      requiredExitingMemory: ['launchedAt'],
      tools: [],
      prompt:
        'Read back the segment, the budget and the approved creative, then ask for the go. On a yes, launch. On anything else, hold and say what is still outstanding.',
    },
  ],
};

// ---------------------------------------------------------------------------
// healthcare_agent, 4 states
// ---------------------------------------------------------------------------

const HEALTHCARE_AGENT = {
  agentId: 'healthcare_agent',
  displayName: 'Healthcare Agent',
  iconHint: '🩺',
  version: 2,
  activeVersion: 2,
  tagline: 'Triages patient messages and books the right kind of visit.',
  description:
    'Reads an inbound patient message, classifies urgency and care type, checks plan eligibility, and either books a clinic slot or pages the on-call care team.',
  gatheringType: 'personal',
  capabilityName: 'healthcare.triage',
  router: {
    intentDescription:
      'Routes here when a patient describes a symptom, asks to be seen, asks about plan coverage for a visit, or reports something that may need urgent care.',
  },
  voicePreamble: {
    tone: 'calm, plain, never alarming',
    bannedPhrases: ['you probably have', 'that sounds serious'],
    preferredPhrases: ['let me get you to the right person', 'I can check what your plan covers'],
  },
  initialState: 'INTAKE_TRIAGE',
  completedStates: ['visit_booked'],
  escalatedStates: ['care_escalated'],
  receivesHandoffs: ['relay_agent'],
  initiatesHandoffs: [],
  ownedArtifacts: ['patient_intake', 'eligibility_result', 'scheduled_visit', 'care_escalation'],
  states: [
    {
      name: 'INTAKE_TRIAGE',
      label: 'Triage the message',
      artifact: 'patient_intake',
      narrationTone: 'calm',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['ELIGIBILITY_CHECK', 'CARE_ESCALATION'],
      requiredEnteringMemory: [],
      requiredExitingMemory: ['urgency', 'careType'],
      tools: ['triagePatientIntake'],
      // Empty prompt. Seeded so the NO_PROMPT rule fires on first render.
      prompt: '',
    },
    {
      name: 'ELIGIBILITY_CHECK',
      label: 'Check coverage',
      artifact: 'eligibility_result',
      narrationTone: 'neutral',
      pillStrategy: 'single',
      haltTurn: false,
      allowedExits: ['SCHEDULE_VISIT', 'CARE_ESCALATION'],
      requiredEnteringMemory: ['urgency', 'careType'],
      requiredExitingMemory: ['eligibilityStatus'],
      tools: ['verifyInsuranceEligibility'],
      // Empty prompt. Seeded so the NO_PROMPT rule fires on first render.
      prompt: '',
    },
    {
      name: 'SCHEDULE_VISIT',
      label: 'Book the visit',
      artifact: 'scheduled_visit',
      narrationTone: 'friendly',
      pillStrategy: 'multi',
      haltTurn: true,
      allowedExits: ['visit_booked'],
      requiredEnteringMemory: ['eligibilityStatus', 'careType'],
      requiredExitingMemory: ['appointmentId'],
      tools: ['bookClinicVisit'],
      prompt:
        'Offer the earliest slots that match the care type, with location and provider named. Confirm the appointment id back to the patient. Do not book without an explicit yes on a specific time.',
    },
    {
      name: 'CARE_ESCALATION',
      label: 'Escalate to the care team',
      artifact: 'care_escalation',
      narrationTone: 'calm',
      pillStrategy: 'single',
      haltTurn: false,
      allowedExits: ['care_escalated'],
      requiredEnteringMemory: ['urgency'],
      requiredExitingMemory: ['escalationId'],
      tools: ['escalateToCareTeam'],
      prompt:
        'Page the on-call team with the triage summary and tell the patient plainly that a person is being brought in. Never speculate about a diagnosis while escalating.',
    },
  ],
};

// ---------------------------------------------------------------------------
// The remaining eight agents. List entries only, counts without states.
// The /full endpoint is what would hydrate these.
// ---------------------------------------------------------------------------

const listOnly = (agentId, displayName, iconHint, version, stateCount, issueCount, tagline) => ({
  agentId,
  displayName,
  iconHint,
  version,
  activeVersion: version,
  tagline,
  states: [],
  stateCount,
  issueCount,
});

export const AGENTS = [
  TRAVEL_BOOKING_AGENT,
  MARKETING_AGENT,
  HEALTHCARE_AGENT,
  listOnly('food_agent', 'Food Agent', '🍕', 12, 9, 1, 'Orders meals, snacks and team treats for any office.'),
  listOnly('gifting_agent', 'Gifting Agent', '🎁', 8, 7, 0, 'Sources, budgets and ships gifts on behalf of a team.'),
  listOnly('billing_recovery_agent', 'Billing Recovery Agent', '💳', 5, 6, 3, 'Restores service when a payment fails or a card is declined.'),
  listOnly('relay_agent', 'Relay Agent', '📡', 14, 4, 0, 'Routes an unmatched request to the agent that owns it.'),
  listOnly('facilities_agent', 'Facilities Agent', '🏢', 3, 8, 2, 'Handles building access, desk moves and site requests.'),
  listOnly('courier_dispatch_agent', 'Courier Dispatch Agent', '🚚', 6, 10, 4, 'Dispatches and tracks same-day couriers against a route.'),
  listOnly('onboarding_agent', 'Onboarding Agent', '🧳', 4, 11, 1, 'Walks a new hire through day-one setup and equipment.'),
  listOnly('expense_report_agent', 'Expense Report Agent', '🧾', 9, 6, 2, 'Builds and submits expense reports from receipts and trips.'),
];

export const AGENT_BY_ID = Object.fromEntries(AGENTS.map((a) => [a.agentId, a]));

export default AGENTS;
